import type { LocationPoint, Venue } from '../planning.ts'
import { plannerApiPayload, plannerApiVersion } from '../planner-api.ts'

export type RoadProfile = 'car' | 'bike' | 'foot'

export interface RoadGeometry {
  duration: number
  coordinates: Array<[number, number]>
}

export async function roadGeometry(
  profile: RoadProfile,
  origin: Pick<LocationPoint, 'lat' | 'lon'>,
  destination: Pick<LocationPoint, 'lat' | 'lon'>,
  signal?: AbortSignal,
): Promise<RoadGeometry> {
  const response = await fetch('/api/routes', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-meet-halfway-api-version': plannerApiVersion },
    body: JSON.stringify({ operation: 'route', profile, origin, destination }),
    signal,
  })
  const payload = await plannerApiPayload<Partial<RoadGeometry> & { error?: string }>(response)
  if (!response.ok) throw new Error(payload.error ?? 'Route preview failed')
  if (!Number.isFinite(payload.duration) || !Array.isArray(payload.coordinates)
    || payload.coordinates.length < 2) throw new Error('Route preview returned an invalid result.')
  return payload as RoadGeometry
}

export async function roadMatrix(
  profile: RoadProfile,
  origins: LocationPoint[],
  destinations: Venue[],
): Promise<Array<Array<number | null>>> {
  const maximumOrigins = profile === 'foot' ? 40 : 6
  const maximumDestinations = profile === 'foot' ? 40 : 25
  const result: Array<Array<number | null>> = origins.map(() => [])
  for (let originOffset = 0; originOffset < origins.length; originOffset += maximumOrigins) {
    const originBatch = origins.slice(originOffset, originOffset + maximumOrigins)
    for (let destinationOffset = 0; destinationOffset < destinations.length;
      destinationOffset += maximumDestinations) {
      const batch = await requestRoadMatrix(
        profile,
        originBatch,
        destinations.slice(destinationOffset, destinationOffset + maximumDestinations),
      )
      batch.forEach((row, index) => result[originOffset + index].push(...row))
    }
  }
  return result
}

async function requestRoadMatrix(
  profile: RoadProfile,
  origins: LocationPoint[],
  destinations: Venue[],
): Promise<Array<Array<number | null>>> {
  const response = await fetch('/api/routes', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-meet-halfway-api-version': plannerApiVersion },
    body: JSON.stringify({
      profile,
      origins: origins.map(({ lat, lon }) => ({ lat, lon })),
      destinations: destinations.map(({ lat, lon }) => ({ lat, lon })),
    }),
  })
  const payload = await plannerApiPayload<{ durations?: number[][]; error?: string }>(response)
  if (!response.ok) throw new Error(payload.error ?? 'Road routing failed')
  if (!payload.durations || payload.durations.length !== origins.length
    || payload.durations.some((row) => row.length !== destinations.length)) {
    throw new Error('Road routing returned an invalid result.')
  }
  return payload.durations.map((row) => row.map((duration) => duration < 0 ? null : duration))
}
